<div class="operator-page-container">
  <!-- ── DESKTOP: page header + table ─────────────────────────────────── -->
  <div class="fe-desktop-only">
  <div class="op-page-header-row">
    <div class="op-dept-pill" [attr.title]="operatorBlockDisplay().title || null">
      <i class="fa-solid fa-location-dot"></i>
      <div class="op-dept-pill-text">
        <span class="op-pill-block">{{ operatorBlockDisplay().primary }}</span>
        <span class="op-pill-dept">{{ (operatorDeptDisplay() || 'No Department') | uppercase }}</span>
      </div>
    </div>
    <div class="op-stat-row">
      <button type="button"
              class="op-stat-card op-pending"
              (click)="selectStat('pending')"
              title="Open list of projects pending approval">
        <i class="fa-solid fa-hourglass-half op-stat-icon"></i>
        <div class="op-stat-body">
          <span class="op-stat-label">Pending</span>
          <span class="op-stat-value">{{ pendingProjectsCount() }}</span>
        </div>
      </button>
      <button type="button"
              class="op-stat-card op-approved"
              (click)="selectStat('approved')"
              title="Open list of approved projects">
        <i class="fa-solid fa-circle-check op-stat-icon"></i>
        <div class="op-stat-body">
          <span class="op-stat-label">Approved</span>
          <span class="op-stat-value">{{ approvedProjectsCount() }}</span>
        </div>
      </button>
      <button type="button"
              class="op-stat-card op-rejected"
              (click)="selectStat('rejected')"
              title="Open list of rejected / returned projects">
        <i class="fa-solid fa-circle-xmark op-stat-icon"></i>
        <div class="op-stat-body">
          <span class="op-stat-label">Rejected</span>
          <span class="op-stat-value">{{ rejectedProjectsCount() }}</span>
        </div>
      </button>
    </div>
  </div>

  <!-- ── SINGLE-SCREEN PROJECT TABLE ── -->
  <div class="op-table-section">
    <div class="op-section-title">
      <i class="fa-solid fa-list-check"></i>
      <span>{{ projectFilterHeadline() }}</span>
    </div>

    <!-- Toolbar: search + filters + reset -->
    <div class="op-toolbar">
      <div class="op-toolbar-search">
        <i class="fa-solid fa-magnifying-glass"></i>
        <input type="text"
               [ngModel]="projectSearch()"
               (ngModelChange)="projectSearch.set($event); opPage.set(1)"
               placeholder="Search name, scheme, block, executant, ID…" />
      </div>
      <select class="op-toolbar-select"
              [ngModel]="projectStatusFilter()"
              (ngModelChange)="projectStatusFilter.set($event); opPage.set(1)"
              aria-label="Filter by status">
        <option value="">All Status</option>
        <option value="Planned">Planned</option>
        <option value="In Progress">In Progress</option>
        <option value="Completed">Completed</option>
        <option value="Stuck">Stuck</option>
      </select>
      <select class="op-toolbar-select"
              [ngModel]="blockFilter()"
              (ngModelChange)="blockFilter.set($event); opPage.set(1)"
              aria-label="Filter by block">
        <option value="">All Blocks</option>
        @for (blk of locationBlocks(); track blk) {
          <option [value]="blk">{{ blk }}</option>
        }
      </select>
      <select class="op-toolbar-select"
              [ngModel]="executantFilter()"
              (ngModelChange)="executantFilter.set($event); opPage.set(1)"
              aria-label="Filter by executant">
        <option value="">All Executants</option>
        @for (name of executantNames(); track name) {
          <option [value]="name">{{ name }}</option>
        }
      </select>
      <button class="op-toolbar-btn" (click)="opResetTableFilters()" title="Clear filters">
        <i class="fa-solid fa-xmark"></i>
      </button>
      <span class="op-toolbar-count">{{ filteredProjectRows().length }} of {{ tableBaseRowsCount() }} projects</span>
    </div>

    <!-- Project table (sticky header, horizontally scrollable) -->
    <div class="op-projects-wrap">
      <table class="op-projects-table">
        <thead>
          <tr>
            <th class="op-sl">#</th>
            <th class="op-col-name">Project Name</th>
            <th class="op-col-scheme">Scheme</th>
            <th class="op-col-gp">GP Name</th>
            <th class="op-num op-col-len">Length (km)</th>
            <th class="op-num op-col-cost">Cost (₹L)</th>
            <th class="op-col-status">Status</th>
            <th class="op-col-date">Last Updated</th>
            <th class="op-actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (p of opPagedProjects; track p.id; let i = $index) {
            <tr [class.row-phantom]="p.isPhantom"
                [class.row-pending]="p.approvalStatus === 'PENDING'"
                [class.row-rejected]="p.approvalStatus === 'REJECTED'">
              <td class="op-sl">{{ opSlForIndex(i) }}</td>
              <td class="op-col-name">
                <div class="op-name-cell">
                  <button type="button" class="op-name-link"
                          (click)="viewProjectRow(p)"
                          title="View project details">{{ p.name }}</button>
                  <div class="op-name-tags">
                    @if (p.recurringIntervention) {
                      <span class="op-mini-tag op-tag-rec" title="Recurring Intervention">
                        <i class="fa-solid fa-rotate"></i> REC
                      </span>
                    }
                    @if (p.approvalStatus === 'PENDING') {
                      <span class="op-mini-tag op-tag-pending" title="Pending approval">
                        <i class="fa-solid fa-hourglass-half"></i> Pending
                      </span>
                    }
                    @if (p.approvalStatus === 'REJECTED') {
                      <span class="op-mini-tag op-tag-rejected" title="Rejected — action required">
                        <i class="fa-solid fa-circle-xmark"></i> Rejected
                      </span>
                    }
                  </div>
                </div>
              </td>
              <td class="op-col-scheme" title="{{ p.scheme || '—' }}">
                <span class="op-scheme-chip">{{ schemeShortName(p.scheme) }}</span>
              </td>
              <td class="op-col-gp">{{ p.gp || '—' }}</td>
              <td class="op-num op-col-len">{{ p.lengthKm != null ? (p.lengthKm | number:'1.1-2') : '—' }}</td>
              <td class="op-num op-col-cost">{{ p.costLakh | number:'1.0-2' }}</td>
              <td class="op-col-status"><span [class]="projectStatusClass(p.status)">{{ p.status }}</span></td>
              <td class="op-col-date">{{ lastUpdatedOf(p) }}</td>
              <td class="op-actions-col">
                <button class="op-act op-act-edit"
                        (click)="editProjectRow(p)"
                        [disabled]="p.isPhantom || !!p.pendingRequest"
                        title="Edit project — changes go to approval">
                  <i class="fa-solid fa-pen-to-square"></i>
                </button>
                <div class="op-act-more-wrap">
                  <button class="op-act" title="More actions" [disabled]="p.isPhantom || !!p.pendingRequest">
                    <i class="fa-solid fa-ellipsis-vertical"></i>
                  </button>
                  <div class="op-act-dropdown">
                    <button class="op-drop-item op-drop-delete"
                            (click)="deleteProjectRow(p)"
                            [disabled]="p.isPhantom || !!p.pendingRequest"
                            title="Delete project — sends to approval">
                      <i class="fa-solid fa-trash-can"></i> Delete
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          }
          @if (filteredProjectRows().length === 0) {
            <tr>
              <td colspan="9" class="op-empty-row">
                <i class="fa-regular fa-folder-open"></i>
                <p>No projects match the current filters.</p>
                @if (projectSearch() || projectStatusFilter() || statFilter()) {
                  <button class="op-toolbar-btn" (click)="opResetTableFilters()">Clear filters</button>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <!-- Pagination footer -->
    @if (filteredProjectRows().length > 0) {
      <div class="op-pagination">
        <div class="op-pagination-left">
          <span class="op-pagination-range">{{ opPageRange }}</span>
          <label class="op-page-size">
            <span>Rows:</span>
            <select [ngModel]="opPageSize()" (ngModelChange)="opSetPageSize($event)">
              <option [ngValue]="10">10</option>
              <option [ngValue]="25">25</option>
              <option [ngValue]="50">50</option>
              <option [ngValue]="100">100</option>
            </select>
          </label>
        </div>
        <div class="op-pagination-right">
          <button class="op-page-btn" (click)="opGoToPage(1)" [disabled]="opPage() === 1" title="First page">
            <i class="fa-solid fa-angles-left"></i>
          </button>
          <button class="op-page-btn" (click)="opPrevPage()" [disabled]="opPage() === 1" title="Previous page">
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <span class="op-page-indicator">Page {{ opPage() }} / {{ opTotalPages }}</span>
          <button class="op-page-btn" (click)="opNextPage()" [disabled]="opPage() === opTotalPages" title="Next page">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
          <button class="op-page-btn" (click)="opGoToPage(opTotalPages)" [disabled]="opPage() === opTotalPages" title="Last page">
            <i class="fa-solid fa-angles-right"></i>
          </button>
        </div>
      </div>
    }
  </div>
  </div><!-- /fe-desktop-only -->

  <!-- ── MOBILE: compact operator console ───────────────────────────── -->
  <div class="fe-mobile-only op-mobile-stack">

    <div class="op-m-dept" [attr.title]="operatorBlockDisplay().title || null">
      <i class="fa-solid fa-location-dot"></i>
      <div class="op-dept-pill-text">
        <span class="op-pill-block">{{ operatorBlockDisplay().primary }}</span>
        <span class="op-pill-dept">{{ (operatorDeptDisplay() || 'No Department') | uppercase }}</span>
      </div>
    </div>

    <div class="op-m-kpi-row">
      <button type="button"
              class="op-m-kpi pending"
              [class.active]="statModalFilter() === 'pending'"
              (click)="selectStat('pending')"
              aria-label="Pending projects">
        <i class="fa-solid fa-hourglass-half"></i>
        <span class="op-m-kpi-val">{{ pendingProjectsCount() }}</span>
      </button>
      <button type="button"
              class="op-m-kpi approved"
              [class.active]="statModalFilter() === 'approved'"
              (click)="selectStat('approved')"
              aria-label="Approved projects">
        <i class="fa-solid fa-circle-check"></i>
        <span class="op-m-kpi-val">{{ approvedProjectsCount() }}</span>
      </button>
      <button type="button"
              class="op-m-kpi rejected"
              [class.active]="statModalFilter() === 'rejected'"
              (click)="selectStat('rejected')"
              aria-label="Rejected projects">
        <i class="fa-solid fa-circle-xmark"></i>
        <span class="op-m-kpi-val">{{ rejectedProjectsCount() }}</span>
      </button>
    </div>

    <div class="op-m-filters">
      <div class="op-m-search">
        <i class="fa-solid fa-magnifying-glass"></i>
        <input type="search"
               [ngModel]="projectSearch()"
               (ngModelChange)="projectSearch.set($event); opPage.set(1)"
               placeholder="Search Project..." />
        @if (projectSearch()) {
          <button type="button" class="op-m-search-clear" (click)="projectSearch.set(''); opPage.set(1)" aria-label="Clear search">
            <i class="fa-solid fa-xmark"></i>
          </button>
        }
      </div>
      <div class="op-m-select-row">
        <select class="op-m-select"
                [ngModel]="projectStatusFilter()"
                (ngModelChange)="projectStatusFilter.set($event); opPage.set(1)"
                aria-label="Filter by status">
          <option value="">Status</option>
          <option value="Planned">Planned</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
          <option value="Stuck">Stuck</option>
        </select>
        <select class="op-m-select"
                [ngModel]="blockFilter()"
                (ngModelChange)="blockFilter.set($event); opPage.set(1)"
                aria-label="Filter by block">
          <option value="">Block</option>
          @for (blk of locationBlocks(); track blk) {
            <option [value]="blk">{{ blk }}</option>
          }
        </select>
      </div>
      <select class="op-m-select op-m-select-full"
              [ngModel]="executantFilter()"
              (ngModelChange)="executantFilter.set($event); opPage.set(1)"
              aria-label="Filter by executant">
        <option value="">Executant</option>
        @for (name of executantNames(); track name) {
          <option [value]="name">{{ name }}</option>
        }
      </select>
    </div>

    <h3 class="op-m-section-title">Projects ({{ filteredProjectRows().length }})</h3>

    @if (filteredProjectRows().length === 0) {
      <div class="op-m-empty">
        <i class="fa-solid fa-folder-open"></i>
        <p>No Projects Found</p>
        <span>Adjust Filters</span>
        @if (projectSearch() || projectStatusFilter() || blockFilter() || executantFilter()) {
          <button type="button" class="op-m-empty-btn" (click)="opResetTableFilters()">Clear filters</button>
        }
      </div>
    } @else {
      <div class="op-m-project-list">
        @for (p of opPagedProjects; track p.id) {
          <article class="op-m-proj-card"
                   [class.row-phantom]="p.isPhantom"
                   [class.row-rejected]="p.approvalStatus === 'REJECTED'">
            <button type="button" class="op-m-proj-main" (click)="viewProjectRow(p)">
              <div class="op-m-proj-head">
                <h4 class="op-m-proj-name">{{ p.name }}</h4>
                <i class="fa-solid fa-chevron-right op-m-proj-chevron"></i>
              </div>
              <div class="op-m-proj-scheme-row">
                @if (p.scheme && p.scheme !== '—') {
                  <span class="op-m-scheme">{{ schemeShortName(p.scheme) }}</span>
                }
                @if (p.recurringIntervention) {
                  <span class="op-m-badge rec"><i class="fa-solid fa-rotate"></i> REC</span>
                }
                @if (p.approvalStatus === 'REJECTED') {
                  <span class="op-m-badge rejected">Rejected</span>
                }
              </div>
              @if (p.gp) {
                <p class="op-m-proj-meta">
                  <i class="fa-solid fa-location-dot"></i>
                  {{ p.gp }}
                </p>
              }
              <p class="op-m-proj-cost">₹{{ p.costLakh | number:'1.0-2' }}</p>
              <span [class]="projectStatusClass(p.status)">{{ p.status }}</span>
              <p class="op-m-proj-date">{{ lastUpdatedOf(p) }}</p>
            </button>
            <div class="op-m-proj-actions">
              <button type="button" class="op-m-act"
                      (click)="editProjectRow(p)"
                      [disabled]="p.isPhantom || !!p.pendingRequest"
                      aria-label="Edit project">
                <i class="fa-solid fa-pen-to-square"></i>
              </button>
              <button type="button" class="op-m-act danger"
                      (click)="deleteProjectRow(p)"
                      [disabled]="p.isPhantom || !!p.pendingRequest"
                      aria-label="Delete project">
                <i class="fa-solid fa-trash-can"></i>
              </button>
            </div>
          </article>
        }
      </div>

      <div class="op-m-pagination">
        <button type="button" class="op-m-page-btn" (click)="opPrevPage()" [disabled]="opPage() === 1" aria-label="Previous page">
          <i class="fa-solid fa-chevron-left"></i>
        </button>
        <span class="op-m-page-info">{{ opPageRange }}</span>
        <button type="button" class="op-m-page-btn" (click)="opNextPage()" [disabled]="opPage() === opTotalPages" aria-label="Next page">
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    }
  </div>

  <!-- Submissions Modal -->
  @if (isModalOpen()) {
    <div class="modal-backdrop" (click)="closeModal()">
      <div class="modal-content glass" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3>
            <i class="fa-solid" [class.fa-file-circle-plus]="!isEditing() && !isEditingPendingRow()" [class.fa-pen-to-square]="isEditing() || isEditingPendingRow()"></i>
            <span>
              @if (isEditingPendingRow()) { Edit Pending Request }
              @else if (isEditing()) { Edit Project (Send for Re-Approval) }
              @else { New Project Request }
            </span>
          </h3>
          <button class="btn-close" (click)="closeModal()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body custom-scroll">
          <form [formGroup]="projectForm" class="request-form">
            <!-- Department (read-only — from operator assignment) -->
            <div class="form-group">
              <label>Department <span class="required">*</span></label>
              @if (operatorDeptDisplay()) {
                <div class="dept-readonly" aria-readonly="true">
                  <i class="fa-solid fa-building-columns"></i>
                  <span>{{ operatorDeptDisplay() }}</span>
                </div>
                <input type="hidden" formControlName="deptName" />
              } @else {
                <div class="dept-readonly dept-readonly--empty">
                  <span>No department assigned to your account.</span>
                </div>
              }
              @if (projectForm.get('deptName')?.invalid) {
                <span class="error-text">Contact an administrator to assign a department</span>
              }
            </div>

            <!-- Scheme Selection / New Scheme Toggle -->
            <div class="form-group">
              <div class="label-with-action">
                <label>Scheme <span class="required" *ngIf="!showNewSchemeForm()">*</span></label>
                <button type="button" class="btn-text" (click)="toggleNewSchemeForm()">
                  <i class="fa-solid" [class.fa-plus]="!showNewSchemeForm()" [class.fa-rotate-left]="showNewSchemeForm()"></i>
                  <span>{{ showNewSchemeForm() ? 'Back to List' : 'Add New Scheme' }}</span>
                </button>
              </div>

              <!-- Locked pending-scheme badge (after Submit Scheme for Approval) -->
              @if (pendingSchemeName()) {
                <div class="pending-scheme-card">
                  <div class="pending-scheme-icon"><i class="fa-regular fa-clock"></i></div>
                  <div class="pending-scheme-body">
                    <span class="pending-scheme-name">{{ pendingSchemeName() }}</span>
                    <small>Submitted for approval · the project below will reference this scheme.</small>
                  </div>
                </div>
              } @else if (!showNewSchemeForm()) {
                <select formControlName="schemeName">
                  <option value="">Select Scheme</option>
                  <option *ngFor="let scheme of selectedDeptSchemes()" [value]="scheme.name">{{ scheme.name }}</option>
                </select>
                @if (projectForm.get('schemeName')?.touched && projectForm.get('schemeName')?.invalid) {
                  <span class="error-text">Please select a scheme</span>
                }
              } @else {
                <!-- New Scheme Template Sub-form (Image 2 Style) -->
                <div class="new-scheme-template glass p-4 mb-4">
                  <div class="template-header">
                    <i class="fa-solid fa-circle-plus text-primary"></i>
                    <span class="font-bold text-slate-700">New Scheme Template</span>
                  </div>
                  <div [formGroup]="schemeForm" class="space-y-4 mt-3">
                    <div class="form-group">
                      <label>SCHEME NAME <span class="required">*</span></label>
                      <input type="text" formControlName="name" placeholder="e.g. Gopabandhu Jana Arogya Yojana">
                      @if (schemeForm.get('name')?.touched && schemeForm.get('name')?.invalid) {
                        <span class="error-text">Scheme name is required</span>
                      }
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                      <div class="form-group">
                        <label>TOTAL BUDGET (₹ Lakh)</label>
                        <input type="number" formControlName="totalCost" placeholder="0">
                      </div>
                      <div class="form-group">
                        <label>AMOUNT SPENT (₹ Lakh)</label>
                        <input type="number" formControlName="spent" placeholder="0">
                      </div>
                    </div>
                    <div class="dept-info-tag">
                      <i class="fa-solid fa-helmet-safety"></i>
                      <span>Department: <strong>{{ operatorDeptDisplay() || 'Not assigned' }}</strong></span>
                    </div>

                    <!-- Action: submit just the scheme for approval -->
                    <div class="scheme-cta-row">
                      <button type="button" class="btn-submit-scheme"
                              [disabled]="isSubmittingScheme()"
                              (click)="submitSchemeOnly()">
                        @if (isSubmittingScheme()) {
                          <i class="fa-solid fa-spinner fa-spin"></i> Sending…
                        } @else {
                          <i class="fa-solid fa-paper-plane"></i> Submit Scheme for Approval
                        }
                      </button>
                      <span class="scheme-cta-hint">
                        Send the scheme by itself — or fill in the project below to submit both at once.
                      </span>
                    </div>
                  </div>

                  <!-- Post-submit choice prompt -->
                  @if (schemeCreatedPrompt()) {
                    <div class="scheme-success-card">
                      <div class="scheme-success-icon"><i class="fa-solid fa-circle-check"></i></div>
                      <div class="scheme-success-body">
                        <h4>Scheme sent for approval</h4>
                        <p>
                          <strong>{{ schemeCreatedPrompt()?.schemeName }}</strong> is awaiting your department approver.
                          Do you want to also submit a project under this scheme now?
                        </p>
                        <div class="scheme-success-actions">
                          <button type="button" class="btn-secondary" (click)="declineProjectAndClose()">No, close</button>
                          <button type="button" class="btn-primary"   (click)="continueWithProjectUnderNewScheme()">
                            <i class="fa-solid fa-plus"></i> Yes, add a project
                          </button>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>

            <div class="divider"></div>

            <!-- Project Details -->
            <div class="form-group">
              <label>Project Name <span class="required">*</span></label>
              <input type="text" formControlName="projectName" placeholder="Enter road or project name">
              @if (projectForm.get('projectName')?.touched && projectForm.get('projectName')?.invalid) {
                <span class="error-text">Project name is required (min 3 chars)</span>
              }
            </div>

            <!-- Block + GP -->
            <div class="grid grid-cols-2 gap-4">
              <div class="form-group">
                <label>Block <span class="required">*</span></label>
                <select formControlName="location"
                        (change)="onBlockChange($any($event.target).value)">
                  <option value="">Select block</option>
                  @for (block of locationBlocks(); track block) {
                    <option [value]="block">{{ block }}</option>
                  }
                </select>
                @if (projectForm.get('location')?.touched && projectForm.get('location')?.invalid) {
                  <span class="error-text">Please select a block</span>
                }
              </div>
              <div class="form-group">
                <label>GP (Gram Panchayat) <span class="required">*</span></label>
                <select formControlName="gp">
                  <option value="">{{ isNacBlock() ? 'NAC (auto-filled)' : 'Select GP' }}</option>
                  @for (gp of gpOptions(); track gp) {
                    <option [value]="gp">{{ gp }}</option>
                  }
                </select>
                @if (isNacBlock()) {
                  <span class="op-field-note">
                    <i class="fa-solid fa-circle-info"></i> NAC blocks auto-assign GP as NAC
                  </span>
                }
                @if (projectForm.get('gp')?.touched && projectForm.get('gp')?.invalid) {
                  <span class="error-text">Please select a GP</span>
                }
              </div>
            </div>

            <!-- Status + Year of Creation -->
            <div class="grid grid-cols-2 gap-4">
              <div class="form-group">
                <label>Status <span class="required">*</span></label>
                <select formControlName="status">
                  <option value="Planned">Planned</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Stuck">Stuck</option>
                </select>
              </div>
              <div class="form-group">
                <label>Year of Creation <span class="required">*</span></label>
                <select formControlName="yearOfCreation">
                  <option value="">Select year</option>
                  @for (yr of yearOptions(); track yr) {
                    <option [value]="yr.toString()">{{ yr }}</option>
                  }
                </select>
                @if (projectForm.get('yearOfCreation')?.touched && projectForm.get('yearOfCreation')?.invalid) {
                  <span class="error-text">Please select the year of creation</span>
                }
              </div>
            </div>

            <!-- Total Cost + Amount Spent -->
            <div class="grid grid-cols-2 gap-4">
              <div class="form-group">
                <label>Total Cost (₹ Lakh) <span class="required">*</span></label>
                <input type="number" formControlName="costCr" placeholder="0.00">
                @if (projectForm.get('costCr')?.touched && projectForm.get('costCr')?.invalid) {
                  <span class="error-text">Valid cost is required</span>
                }
              </div>
              <div class="form-group">
                <label>Amount Spent (₹ Lakh)</label>
                <input type="number" formControlName="spentCr" placeholder="0.00">
              </div>
            </div>

            <!-- Road Length (optional) -->
            <div class="form-group">
              <label>
                Road Length (km)
                <span class="op-optional-tag">Optional</span>
              </label>
              <input type="number" formControlName="roadLength" placeholder="e.g. 2.5" min="0" step="0.01">
              @if (projectForm.get('roadLength')?.touched && projectForm.get('roadLength')?.invalid) {
                <span class="error-text">Road length must be 0 or more</span>
              }
            </div>

            <!-- ── Work Photos: Before / During / After ───────────────────── -->
            <div class="form-group">
              <label>
                Work Photos
                <span class="op-optional-tag">Optional · JPG / PNG / WEBP · max 5 MB each · multiple allowed</span>
              </label>
              <div class="op-photo-sections">
                <!-- Before -->
                <div class="op-photo-col">
                  <div class="op-photo-category">
                    <div class="op-photo-cat-label">
                      <i class="fa-solid fa-camera-retro"></i> Before Work
                    </div>
                    <div class="op-photo-thumbs">
                      @for (url of photoBeforePreviews(); track url; let i = $index) {
                        <div class="op-photo-thumb">
                          <img [src]="url" alt="Before photo" loading="lazy">
                          <button type="button" class="op-photo-remove" (click)="removePhoto('before', i)"
                                  title="Remove photo"><i class="fa-solid fa-xmark"></i></button>
                        </div>
                      }
                      <label class="op-photo-add" title="Add before photo">
                        <i class="fa-solid fa-plus"></i>
                        <input type="file" multiple accept="image/jpeg,image/png,image/webp"
                               (change)="onPhotoSelected('before', $event)" style="display:none">
                      </label>
                    </div>
                  </div>
                  <div class="op-photo-date-card">
                    <label>Before Date</label>
                    <input type="date" formControlName="photoDateBefore">
                  </div>
                </div>
                <!-- During -->
                <div class="op-photo-col">
                  <div class="op-photo-category">
                    <div class="op-photo-cat-label">
                      <i class="fa-solid fa-camera"></i> During Work
                    </div>
                    <div class="op-photo-thumbs">
                      @for (url of photoDuringPreviews(); track url; let i = $index) {
                        <div class="op-photo-thumb">
                          <img [src]="url" alt="During photo" loading="lazy">
                          <button type="button" class="op-photo-remove" (click)="removePhoto('during', i)"
                                  title="Remove photo"><i class="fa-solid fa-xmark"></i></button>
                        </div>
                      }
                      <label class="op-photo-add" title="Add during photo">
                        <i class="fa-solid fa-plus"></i>
                        <input type="file" multiple accept="image/jpeg,image/png,image/webp"
                               (change)="onPhotoSelected('during', $event)" style="display:none">
                      </label>
                    </div>
                  </div>
                  <div class="op-photo-date-card">
                    <label>During Date</label>
                    <input type="date" formControlName="photoDateDuring">
                  </div>
                </div>
                <!-- After -->
                <div class="op-photo-col">
                  <div class="op-photo-category">
                    <div class="op-photo-cat-label">
                      <i class="fa-solid fa-flag-checkered"></i> After Work
                    </div>
                    <div class="op-photo-thumbs">
                      @for (url of photoAfterPreviews(); track url; let i = $index) {
                        <div class="op-photo-thumb">
                          <img [src]="url" alt="After photo" loading="lazy">
                          <button type="button" class="op-photo-remove" (click)="removePhoto('after', i)"
                                  title="Remove photo"><i class="fa-solid fa-xmark"></i></button>
                        </div>
                      }
                      <label class="op-photo-add" title="Add after photo">
                        <i class="fa-solid fa-plus"></i>
                        <input type="file" multiple accept="image/jpeg,image/png,image/webp"
                               (change)="onPhotoSelected('after', $event)" style="display:none">
                      </label>
                    </div>
                  </div>
                  <div class="op-photo-date-card">
                    <label>After Date</label>
                    <input type="date" formControlName="photoDateAfter">
                  </div>
                </div>
              </div>
              @if (isUploadingPhotos()) {
                <div class="op-photo-upload-progress">
                  <div class="op-upload-progress-bar">
                    <div class="op-upload-progress-fill" [style.width.%]="photoUploadProgress()"></div>
                  </div>
                  <span class="op-upload-pct">Uploading photos… {{ photoUploadProgress() }}%</span>
                </div>
              }
            </div>

            <div class="divider"></div>

            <!-- ── Executant Details ───────────────────────────────────────── -->
            <div class="op-section-sub-header">
              <i class="fa-solid fa-user-tie"></i>
              <span>Executant / Contractor Details</span>
              <span class="op-optional-tag">Optional</span>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div class="form-group">
                <label>Executant Name</label>
                <input type="text" formControlName="executantName" placeholder="Contractor / agency name">
              </div>
              <div class="form-group">
                <label>Executant Contact</label>
                <input type="tel" formControlName="executantContact" placeholder="Mobile number">
              </div>
            </div>

            <!-- HM Priority / Recurring flags are not editable by operators —
                 those are set by OSD from the department detail view. -->
          </form>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" (click)="closeModal()" [disabled]="isUploadingPhotos()">Cancel</button>
          <button class="btn-primary" [disabled]="isSubmitting() || isUploadingPhotos()" (click)="submitRequest()">
            <i class="fa-solid"
               [class.fa-spinner]="isSubmitting() || isUploadingPhotos()"
               [class.fa-paper-plane]="!isSubmitting() && !isUploadingPhotos()"></i>
            <span>
              @if (isUploadingPhotos()) { Uploading photos… {{ photoUploadProgress() }}% }
              @else if (isSubmitting()) { {{ isEditingPendingRow() ? 'Updating Request...' : isEditing() ? 'Sending for Approval...' : 'Submitting...' }} }
              @else { {{ isEditingPendingRow() ? 'Update Pending Request' : isEditing() ? 'Send Edit for Approval' : 'Submit Request' }} }
            </span>
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ════════════════════════════════════════════════════════════════
       PROJECT PREVIEW MODAL — formatted read-only view for phantom /
       pending project rows (replaces raw JSON compare-changes view)
       ════════════════════════════════════════════════════════════════ -->
  @if (isPreviewOpen() && previewRow()) {
    <div class="op-preview-overlay" (click)="closeProjectPreview()">
      <div class="op-preview-card" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">

        <!-- Header -->
        <div class="op-preview-header">
          <div class="op-preview-header-left">
            <h2 class="op-preview-title">{{ previewRow().name }}</h2>
            <div class="op-preview-chips">
              @if (previewRow().block) {
                <span class="op-preview-chip">
                  <i class="fa-solid fa-location-dot"></i>
                  <span class="op-preview-chip-lbl">Block</span>{{ previewRow().block }}
                </span>
              }
              @if (previewRow().gp) {
                <span class="op-preview-chip">
                  <i class="fa-solid fa-landmark"></i>
                  <span class="op-preview-chip-lbl">GP</span>{{ previewRow().gp }}
                </span>
              }
              @if (previewRow().scheme && previewRow().scheme !== '—') {
                <span class="op-preview-chip">
                  <i class="fa-solid fa-layer-group"></i>
                  <span class="op-preview-chip-lbl">Scheme</span>{{ previewRow().scheme }}
                </span>
              }
              @if (previewRow().dept) {
                <span class="op-preview-chip op-preview-chip-dept">
                  <i class="fa-solid fa-building-columns"></i>{{ previewRow().dept }}
                </span>
              }
            </div>
          </div>
          <div class="op-preview-header-right">
            @if (previewRow().approvalStatus === 'PENDING') {
              <span class="op-preview-status-badge op-preview-badge-pending">
                <i class="fa-solid fa-hourglass-half"></i> Pending Approval
              </span>
            } @else if (previewRow().approvalStatus === 'REJECTED') {
              <span class="op-preview-status-badge op-preview-badge-rejected">
                <i class="fa-solid fa-circle-xmark"></i> Rejected
              </span>
            }
            <button class="op-preview-close" (click)="closeProjectPreview()" aria-label="Close">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="op-preview-body custom-scroll">

          <!-- Stat cards row -->
          <div class="op-preview-cards">

            <div class="op-preview-card-item op-pci-green">
              <div class="op-pci-icon"><i class="fa-solid fa-indian-rupee-sign"></i></div>
              <div class="op-pci-body">
                <p class="op-pci-label">Total Cost</p>
                <p class="op-pci-value">₹{{ previewRow().costLakh | number:'1.0-2' }}<span class="op-pci-unit">L</span></p>
              </div>
            </div>

            <div class="op-preview-card-item op-pci-teal">
              <div class="op-pci-icon"><i class="fa-solid fa-money-bill-transfer"></i></div>
              <div class="op-pci-body">
                <p class="op-pci-label">Amount Spent</p>
                <p class="op-pci-value">₹{{ previewRow().spentLakh | number:'1.0-2' }}<span class="op-pci-unit">L</span></p>
              </div>
            </div>

            <div class="op-preview-card-item op-pci-slate">
              <div class="op-pci-icon"><i class="fa-solid fa-circle-dot"></i></div>
              <div class="op-pci-body">
                <p class="op-pci-label">Status</p>
                <span [class]="projectStatusClass(previewRow().status)">{{ previewRow().status }}</span>
              </div>
            </div>

            <div class="op-preview-card-item op-pci-amber">
              <div class="op-pci-icon"><i class="fa-solid fa-calendar-check"></i></div>
              <div class="op-pci-body">
                <p class="op-pci-label">Year of Creation</p>
                <p class="op-pci-value op-pci-value-md">{{ previewRow().yearOfCreation || '—' }}</p>
              </div>
            </div>

            <div class="op-preview-card-item op-pci-purple">
              <div class="op-pci-icon"><i class="fa-solid fa-road"></i></div>
              <div class="op-pci-body">
                <p class="op-pci-label">Road Length</p>
                @if (previewRow().lengthKm) {
                  <p class="op-pci-value">{{ previewRow().lengthKm | number:'1.1-2' }}<span class="op-pci-unit">km</span></p>
                } @else {
                  <p class="op-pci-na">Not available</p>
                }
              </div>
            </div>

            <div class="op-preview-card-item op-pci-blue">
              <div class="op-pci-icon"><i class="fa-solid fa-user-hard-hat"></i></div>
              <div class="op-pci-body">
                <p class="op-pci-label">Executant Name</p>
                @if (previewRow().executantName) {
                  <p class="op-pci-value op-pci-value-md">{{ previewRow().executantName }}</p>
                } @else {
                  <p class="op-pci-na">Not Assigned</p>
                }
              </div>
            </div>

            <div class="op-preview-card-item op-pci-indigo">
              <div class="op-pci-icon"><i class="fa-solid fa-phone"></i></div>
              <div class="op-pci-body">
                <p class="op-pci-label">Executant Contact</p>
                @if (previewRow().executantContact) {
                  <p class="op-pci-value op-pci-value-md">{{ previewRow().executantContact }}</p>
                } @else {
                  <p class="op-pci-na">Not Assigned</p>
                }
              </div>
            </div>

          </div>

          <!-- Recurring badge -->
          @if (previewRow().recurringIntervention) {
            <div class="op-preview-badges">
              <span class="op-preview-flag op-flag-rec">
                <i class="fa-solid fa-rotate"></i> Recurring Intervention
              </span>
            </div>
          }

          <!-- Site Photos -->
          <div class="op-preview-photos-section">
            <h3 class="op-preview-section-title">
              <i class="fa-solid fa-images"></i> Site Photos
            </h3>
            <div class="op-preview-photos-grid">

              <!-- Before Work -->
              <div class="op-preview-photo-box">
                <div class="op-preview-photo-label op-photo-label-before">
                  <i class="fa-solid fa-hourglass-start"></i> Before Work
                  @if (previewPhotosBefore().length > 0) {
                    <span class="op-preview-photo-badge">{{ previewPhotosBefore().length }}</span>
                  }
                </div>
                @if (previewDateBefore()) {
                  <div class="op-preview-photo-date"><i class="fa-regular fa-calendar"></i> {{ previewDateBefore() | date:'mediumDate' }}</div>
                }
                @if (previewPhotosBefore().length > 0) {
                  <div class="op-preview-photo-frame">
                    <img [src]="previewPhotosBefore()[0]" alt="Before work photo" loading="lazy" />
                    @if (previewPhotosBefore().length > 1) {
                      <div class="op-preview-photo-more">+{{ previewPhotosBefore().length - 1 }} more</div>
                    }
                  </div>
                  @if (previewPhotosBefore().length > 1) {
                    <div class="op-preview-thumb-strip">
                      @for (src of previewPhotosBefore().slice(1); track src; let i = $index) {
                        @if (i < 4) {
                          <div class="op-preview-thumb">
                            <img [src]="src" alt="Before photo" loading="lazy" />
                          </div>
                        }
                      }
                    </div>
                  }
                } @else {
                  <div class="op-preview-photo-placeholder">
                    <i class="fa-regular fa-image"></i>
                    <p>No photo uploaded</p>
                  </div>
                }
              </div>

              <!-- During Work -->
              <div class="op-preview-photo-box">
                <div class="op-preview-photo-label op-photo-label-during">
                  <i class="fa-solid fa-person-digging"></i> During Work
                  @if (previewPhotosDuring().length > 0) {
                    <span class="op-preview-photo-badge">{{ previewPhotosDuring().length }}</span>
                  }
                </div>
                @if (previewDateDuring()) {
                  <div class="op-preview-photo-date"><i class="fa-regular fa-calendar"></i> {{ previewDateDuring() | date:'mediumDate' }}</div>
                }
                @if (previewPhotosDuring().length > 0) {
                  <div class="op-preview-photo-frame">
                    <img [src]="previewPhotosDuring()[0]" alt="During work photo" loading="lazy" />
                    @if (previewPhotosDuring().length > 1) {
                      <div class="op-preview-photo-more">+{{ previewPhotosDuring().length - 1 }} more</div>
                    }
                  </div>
                  @if (previewPhotosDuring().length > 1) {
                    <div class="op-preview-thumb-strip">
                      @for (src of previewPhotosDuring().slice(1); track src; let i = $index) {
                        @if (i < 4) {
                          <div class="op-preview-thumb">
                            <img [src]="src" alt="During photo" loading="lazy" />
                          </div>
                        }
                      }
                    </div>
                  }
                } @else {
                  <div class="op-preview-photo-placeholder">
                    <i class="fa-regular fa-image"></i>
                    <p>No photo uploaded</p>
                  </div>
                }
              </div>

              <!-- After Work -->
              <div class="op-preview-photo-box">
                <div class="op-preview-photo-label op-photo-label-after">
                  <i class="fa-solid fa-circle-check"></i> After Work
                  @if (previewPhotosAfter().length > 0) {
                    <span class="op-preview-photo-badge">{{ previewPhotosAfter().length }}</span>
                  }
                </div>
                @if (previewDateAfter()) {
                  <div class="op-preview-photo-date"><i class="fa-regular fa-calendar"></i> {{ previewDateAfter() | date:'mediumDate' }}</div>
                }
                @if (previewPhotosAfter().length > 0) {
                  <div class="op-preview-photo-frame">
                    <img [src]="previewPhotosAfter()[0]" alt="After work photo" loading="lazy" />
                    @if (previewPhotosAfter().length > 1) {
                      <div class="op-preview-photo-more">+{{ previewPhotosAfter().length - 1 }} more</div>
                    }
                  </div>
                  @if (previewPhotosAfter().length > 1) {
                    <div class="op-preview-thumb-strip">
                      @for (src of previewPhotosAfter().slice(1); track src; let i = $index) {
                        @if (i < 4) {
                          <div class="op-preview-thumb">
                            <img [src]="src" alt="After photo" loading="lazy" />
                          </div>
                        }
                      }
                    </div>
                  }
                } @else {
                  <div class="op-preview-photo-placeholder">
                    <i class="fa-regular fa-image"></i>
                    <p>No photo uploaded</p>
                  </div>
                }
              </div>

            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="op-preview-footer">
          <button class="btn-secondary" (click)="closeProjectPreview()">
            <i class="fa-solid fa-arrow-left"></i> Close
          </button>
          @if (previewRow().isPhantom && previewRow().approvalStatus === 'PENDING') {
            <button class="btn-primary" (click)="editPendingRequest(previewRow())">
              <i class="fa-solid fa-pen-to-square"></i> Edit Pending Request
            </button>
          }
        </div>

      </div>
    </div>
  }

  <!-- Request Detail Modal (read-only) -->
  <app-compare-changes
    [isOpen]="isDetailOpen()"
    [beforeData]="detailBefore()"
    [newData]="detailAfter()"
    [meta]="detailMeta()"
    [readOnly]="true"
    (close)="closeRequestDetail()"
  ></app-compare-changes>

  <!-- Bulk Import Modal -->
  <app-bulk-import-modal
    [isOpen]="isImportOpen()"
    (closed)="onImportClosed($event)"
  ></app-bulk-import-modal>

  <!-- Delete Confirmation Modal (Send Delete Request) -->
  @if (isDeleteConfirmOpen()) {
    <div class="op-modal-overlay" (click)="cancelDelete()">
      <div class="op-modal-card op-modal-card--narrow" (click)="$event.stopPropagation()">
        <div class="op-modal-header">
          <div>
            <h3>
              <i class="fa-solid fa-triangle-exclamation" style="color:#ef4444;"></i>
              Delete Project
            </h3>
            <p>This request will be sent to the approver — the project is NOT deleted yet.</p>
          </div>
          <button class="op-modal-close" (click)="cancelDelete()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="op-modal-body">
          <p style="font-size: 0.95rem; color: #334155;">
            Are you sure you want to delete
            <strong>"{{ pendingDeleteRow()?.name }}"</strong>?
          </p>
          <p style="font-size: 0.85rem; color: #64748b; margin-top: 8px;">
            The project will remain active until your department approver reviews and
            approves this delete request. You can track it under "Pending Approval".
          </p>
        </div>

        <div class="op-modal-footer" style="display:flex; gap: 10px; justify-content: flex-end;">
          <button class="btn-secondary" (click)="cancelDelete()" [disabled]="isSubmittingDelete()">
            Cancel
          </button>
          <button class="btn-primary"
                  style="background:#ef4444; border-color:#ef4444;"
                  (click)="confirmDeleteRequest()"
                  [disabled]="isSubmittingDelete()">
            <i class="fa-solid" [class.fa-spinner]="isSubmittingDelete()" [class.fa-paper-plane]="!isSubmittingDelete()"></i>
            <span>{{ isSubmittingDelete() ? 'Sending...' : 'Send Delete Request' }}</span>
          </button>
        </div>
      </div>
    </div>
  }

  <!-- My Departments Modal -->
  @if (isDepartmentsOpen()) {
    <div class="op-modal-overlay" (click)="closeDepartments()">
      <div class="op-modal-card" (click)="$event.stopPropagation()">
        <div class="op-modal-header">
          <div>
            <h3>My Departments</h3>
            <p>{{ departmentsCount() }} department(s) assigned to you</p>
          </div>
          <button class="op-modal-close" (click)="closeDepartments()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="op-modal-body">
          @if (assignedDepts().length === 0) {
            <div class="op-empty">
              <i class="fa-regular fa-folder-open"></i>
              <p>No departments are assigned to your account yet.</p>
              <small>Contact an administrator to get department access.</small>
            </div>
          } @else {
            <div class="op-dept-grid">
              @for (d of assignedDepts(); track d.name) {
                <div class="op-dept-card">
                  <div class="op-dept-icon" [style.background]="d.color || '#1e40af'">
                    <i [class]="d.icon || 'fa-solid fa-building-columns'"></i>
                  </div>
                  <div class="op-dept-info">
                    <span class="op-dept-name">{{ d.name }}</span>
                    <span class="op-dept-meta">{{ d.schemes.length || 0 }} scheme(s)</span>
                  </div>
                </div>
              }
            </div>
          }
        </div>

        <div class="op-modal-footer">
          <button class="btn-secondary" (click)="closeDepartments()">Close</button>
        </div>
      </div>
    </div>
  }

  <!-- ════════════════════════════════════════════════════════════════
       STAT-CARD POPUP — opens when a Pending / Approved / Rejected
       card is clicked. Shows the matching project rows in a scrollable
       list. Data is derived live from Firestore (`projectRows()`).
       ════════════════════════════════════════════════════════════════ -->
  @if (statModalFilter()) {
    <div class="op-stat-modal-overlay" (click)="closeStatModal()">
      <div class="op-stat-modal-card" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
        <div class="op-stat-modal-header"
             [class.is-pending]="statModalFilter() === 'pending'"
             [class.is-approved]="statModalFilter() === 'approved'"
             [class.is-rejected]="statModalFilter() === 'rejected'">
          <div class="op-stat-modal-title-wrap">
            <div class="op-stat-modal-icon">
              @switch (statModalFilter()) {
                @case ('pending')  { <i class="fa-solid fa-hourglass-half"></i> }
                @case ('approved') { <i class="fa-solid fa-circle-check"></i> }
                @case ('rejected') { <i class="fa-solid fa-circle-xmark"></i> }
              }
            </div>
            <div>
              <h3>{{ statModalTitle() }}</h3>
              <p>{{ statModalRows().length }} project{{ statModalRows().length === 1 ? '' : 's' }} · Live from Firestore</p>
            </div>
          </div>
          <button class="op-stat-modal-close" (click)="closeStatModal()" aria-label="Close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="op-stat-modal-body">
          @if (statModalRows().length === 0) {
            <div class="op-stat-modal-empty">
              <i class="fa-regular fa-folder-open"></i>
              <p>No projects to show.</p>
            </div>
          } @else {
            <table class="op-stat-modal-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Project Name</th>
                  <th>Scheme</th>
                  <th>GP Name</th>
                  <th class="op-num">Cost (₹L)</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  @if (statModalFilter() === 'pending') {
                    <th class="op-stat-actions-col">Actions</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (p of statModalRows(); track p.id; let i = $index) {
                  <tr [class.row-phantom]="p.isPhantom">
                    <td class="op-num-cell">{{ i + 1 }}</td>
                    <td>
                      @if (statModalFilter() === 'pending') {
                        <button type="button" class="op-name-link"
                                (click)="viewProjectRow(p); closeStatModal()">
                          {{ p.name }}
                        </button>
                      } @else {
                        <span class="op-name-plain">{{ p.name }}</span>
                      }
                      @if (p.recurringIntervention) {
                        <span class="op-mini-tag op-tag-rec"><i class="fa-solid fa-rotate"></i> REC</span>
                      }
                    </td>
                    <td title="{{ p.scheme || '—' }}">{{ schemeShortName(p.scheme) }}</td>
                    <td>{{ p.gp || '—' }}</td>
                    <td class="op-num">{{ p.costLakh | number:'1.0-2' }}</td>
                    <td><span [class]="projectStatusClass(p.status)">{{ p.status }}</span></td>
                    <td>{{ lastUpdatedOf(p) }}</td>
                    @if (statModalFilter() === 'pending') {
                      <td class="op-stat-actions-col">
                        <button type="button" class="op-act op-act-delete"
                                (click)="deleteStatRow(p)"
                                title="Withdraw this pending request">
                          <i class="fa-solid fa-trash-can"></i>
                        </button>
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>

        <div class="op-stat-modal-footer">
          <button class="btn-secondary" (click)="closeStatModal()">Close</button>
        </div>
      </div>
    </div>
  }
</div>
